import { motion } from 'framer-motion';

type Props = {
  title?: string;
  description?: string;
};

export default function NoGoalsCard({
  title = "No goals yet",
  description = "You don't have any goals assigned.",
}: Props) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className="max-w-xl mx-auto bg-white/60 backdrop-blur-sm border border-gray-100 shadow-lg rounded-2xl p-6 sm:p-8 flex gap-6 items-center"
      role="region"
      aria-label="No goals"
    >
      {/* Illustration */}
      <motion.div
        whileHover={{ scale: 1.03 }}
        className="flex-none"
        aria-hidden
      >
        <svg
          width="96"
          height="96"
          viewBox="0 0 96 96"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="block"
        >
          <defs>
            <linearGradient id="g1" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0" stopColor="#7C3AED" />
              <stop offset="1" stopColor="#06B6D4" />
            </linearGradient>
            <linearGradient id="g2" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0" stopColor="#A78BFA" stopOpacity="0.9" />
              <stop offset="1" stopColor="#60A5FA" stopOpacity="0.9" />
            </linearGradient>
          </defs>

          {/* soft background blob */}
          <motion.path
            d="M12 60c8-24 40-36 64-28s18 40-4 52S12 60 12 60z"
            fill="url(#g2)"
            opacity="0.15"
            initial={{ scale: 0.98 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.8 }}
          />

          {/* user circle */}
          <circle cx="48" cy="36" r="14" fill="url(#g1)" />

          {/* shoulders */}
          <path
            d="M28 74c4-10 14-16 20-16s16 6 20 16"
            stroke="#0F172A"
            strokeOpacity="0.08"
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />

          {/* sparkle (minimal) */}
          <g transform="translate(74,12)">
            <rect
              x="-2"
              y="-7"
              width="4"
              height="14"
              rx="1"
              fill="#FDE68A"
              opacity="0.95"
            />
            <rect
              x="-7"
              y="-2"
              width="14"
              height="4"
              rx="1"
              fill="#FB7185"
              opacity="0.95"
              transform="rotate(45 -0.5 -0.5)"
            />
          </g>
        </svg>
      </motion.div>
      <div className="flex-1 min-w-0">
        <h3 className="text-lg font-semibold text-slate-900 tracking-tight">
          {title}
        </h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          {description}
        </p>
        <div className="mt-4">
          <p className="text-xs text-slate-400">
            Pro tip: set measurable targets and update tasks regularly.
          </p>
        </div>
      </div>
    </motion.div>
  );
}
